<template>
  <v-container fluid class="ncrm-board">

    <!-- ── Breadcrumb strip ──────────────────────────────── -->
    <div class="ncrm-board__header" data-tour="board-header">
      <div class="ncrm-board__header-left">
        <v-icon class="ncrm-board__header-icon" size="16">mdi-view-dashboard-outline</v-icon>
        <h1 class="ncrm-board__title">{{ $t("ncrm.board.title") }}</h1>
        <span class="ncrm-board__subtitle">{{ $t("ncrm.board.subtitle") }}</span>
      </div>
      <div class="ncrm-board__header-right">
        <v-btn variant="text" color="default" size="small" data-tour="board-widgets" @click="widgetManagerDialog = true">
          <v-icon start size="16">mdi-view-dashboard-edit-outline</v-icon>
          {{ $t("ncrm.board.widgets.configure") }}
        </v-btn>
        <PageHelpButton />
      </div>
    </div>

    <!-- ── KPI strip ──────────────────────────────────────── -->
    <div class="ncrm-board__kpi-strip" data-tour="board-kpi">
      <!-- Activități overdue -->
      <button class="ncrm-kpi-tile ncrm-kpi-tile--error" @click="openOverdueList">
        <div class="ncrm-kpi-tile__head">
          <span class="ncrm-kpi-tile__eyebrow">{{ $t("ncrm.board.widgets.overdue.title") }}</span>
          <v-icon size="16" class="ncrm-kpi-tile__head-icon">mdi-alert-circle-outline</v-icon>
        </div>
        <div class="ncrm-kpi-tile__value">
          <v-skeleton-loader v-if="widgetsLoading" type="text" width="48" height="36" class="d-inline-block" />
          <span v-else>{{ activityWidgets.overdue.count }}</span>
        </div>
        <div class="ncrm-kpi-tile__foot">
          <svg class="ncrm-kpi-sparkline" viewBox="0 0 72 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polyline points="0,20 10,17 20,20 30,10 40,13 50,5 62,3 72,0"
              fill="none" stroke="currentColor" stroke-opacity="0.75" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </button>

      <!-- Activități viitoare 72h -->
      <button class="ncrm-kpi-tile ncrm-kpi-tile--warning" @click="openUpcomingList">
        <div class="ncrm-kpi-tile__head">
          <span class="ncrm-kpi-tile__eyebrow">{{ $t("ncrm.board.widgets.upcoming.title") }}</span>
          <v-icon size="16" class="ncrm-kpi-tile__head-icon">mdi-clock-time-four-outline</v-icon>
        </div>
        <div class="ncrm-kpi-tile__value">
          <v-skeleton-loader v-if="widgetsLoading" type="text" width="48" height="36" class="d-inline-block" />
          <span v-else>{{ activityWidgets.upcoming.count }}</span>
        </div>
        <div class="ncrm-kpi-tile__foot">
          <svg class="ncrm-kpi-sparkline" viewBox="0 0 72 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polyline points="0,4 10,8 20,4 30,14 40,10 50,18 62,22 72,26"
              fill="none" stroke="currentColor" stroke-opacity="0.75" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </button>

      <!-- Timp sesiune azi -->
      <div class="ncrm-kpi-tile ncrm-kpi-tile--neutral">
        <div class="ncrm-kpi-tile__head">
          <span class="ncrm-kpi-tile__eyebrow">{{ $t("ncrm.board.widgets.smartTracker.dayTimer") }}</span>
          <v-icon size="16" class="ncrm-kpi-tile__head-icon">mdi-timer-outline</v-icon>
        </div>
        <div class="ncrm-kpi-tile__value ncrm-kpi-tile__value--mono">
          <v-skeleton-loader v-if="widgetsLoading" type="text" width="72" height="36" class="d-inline-block" />
          <span v-else>{{ formatDuration(smartTrackerDaySecondsDisplay) }}</span>
        </div>
        <div class="ncrm-kpi-tile__foot">
          <svg class="ncrm-kpi-sparkline" viewBox="0 0 72 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polyline points="0,26 10,26 20,18 30,12 40,8 50,12 62,8 72,5"
              fill="none" stroke="currentColor" stroke-opacity="0.55" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>

      <!-- Sesiuni azi -->
      <div class="ncrm-kpi-tile ncrm-kpi-tile--teal">
        <div class="ncrm-kpi-tile__head">
          <span class="ncrm-kpi-tile__eyebrow">{{ $t("ncrm.board.widgets.smartTracker.title") }}</span>
          <v-icon size="16" class="ncrm-kpi-tile__head-icon">mdi-calendar-check-outline</v-icon>
        </div>
        <div class="ncrm-kpi-tile__value">
          <v-skeleton-loader v-if="widgetsLoading" type="text" width="48" height="36" class="d-inline-block" />
          <span v-else>{{ smartTrackerSummary.sessions_count }}</span>
        </div>
        <div class="ncrm-kpi-tile__foot">
          <svg class="ncrm-kpi-sparkline" viewBox="0 0 72 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polyline points="0,20 10,16 20,20 30,10 40,14 50,6 62,10 72,10"
              fill="none" stroke="currentColor" stroke-opacity="0.6" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </div>

    <div data-tour="board-modules">
      <NcrmBoardModuleTiles :items="moduleNavItems" :loading="widgetsLoading" @navigate="goToModule" />
    </div>

    <!-- ── Activity widgets ───────────────────────────────── -->
    <div v-if="canAccessActivityWidgets" class="ncrm-board__activities">

      <!-- Overdue card -->
      <div v-if="activeWidgetKeys.includes('overdue')" class="ncrm-widget-card">
        <div class="ncrm-widget-card__header">
          <div class="ncrm-widget-card__header-left">
            <v-icon size="16" color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
            <span class="ncrm-widget-card__title">{{ $t("ncrm.board.widgets.overdue.title") }}</span>
          </div>
          <v-chip size="x-small" color="error">{{ activityWidgets.overdue.count }}</v-chip>
        </div>

        <div class="ncrm-widget-card__body">
          <v-skeleton-loader v-if="widgetsLoading" type="list-item-two-line@5" />
          <div v-else-if="!activityWidgets.overdue.items.length" class="ncrm-widget-card__empty">
            <v-icon size="32" class="ncrm-widget-card__empty-icon">mdi-check-circle-outline</v-icon>
            <span>{{ $t("ncrm.board.widgets.overdue.empty") }}</span>
          </div>
          <div v-else class="ncrm-activity-list">
            <div
              v-for="item in activityWidgets.overdue.items"
              :key="`overdue-${item.id}`"
              class="ncrm-activity-row"
              @click="openSource(item)"
            >
              <div class="ncrm-activity-row__icon ncrm-activity-row__icon--error">
                <v-icon size="13">{{ activityTypeIcon(item.activity_type) }}</v-icon>
              </div>
              <div class="ncrm-activity-row__body">
                <div class="ncrm-activity-row__title">{{ resolveActivityTitle(item) }}</div>
                <div class="ncrm-activity-row__meta">
                  <span>{{ activityTypeLabel(item.activity_type) }}</span>
                  <span class="ncrm-activity-row__sep">·</span>
                  <span class="ncrm-activity-row__date ncrm-activity-row__date--error">{{ formatDateTime(item.due_at) }}</span>
                </div>
              </div>
              <span class="ncrm-activity-row__company">{{ resolveCompanyLabel(item) }}</span>
              <v-icon size="13" class="ncrm-activity-row__chevron">mdi-chevron-right</v-icon>
            </div>
          </div>
        </div>

        <div class="ncrm-widget-card__footer">
          <button class="ncrm-widget-card__footer-link" @click="openOverdueList">
            <v-icon size="13" class="mr-1">mdi-open-in-new</v-icon>
            {{ $t("ncrm.board.widgets.overdue.openList") }}
          </button>
          <span v-if="activityWidgets.overdue.items.length" class="ncrm-widget-card__footer-meta">
            <v-icon size="11" style="opacity:0.38">mdi-clock-outline</v-icon>
            {{ formatDateTime(activityWidgets.overdue.items[activityWidgets.overdue.items.length - 1]?.due_at) }}
          </span>
        </div>
      </div>

      <!-- Upcoming card -->
      <div v-if="activeWidgetKeys.includes('upcoming')" class="ncrm-widget-card">
        <div class="ncrm-widget-card__header">
          <div class="ncrm-widget-card__header-left">
            <v-icon size="16" color="warning" class="mr-1">mdi-clock-time-four-outline</v-icon>
            <span class="ncrm-widget-card__title">{{ $t("ncrm.board.widgets.upcoming.title") }}</span>
          </div>
          <v-chip size="x-small" color="warning">{{ activityWidgets.upcoming.count }}</v-chip>
        </div>

        <div class="ncrm-widget-card__body">
          <v-skeleton-loader v-if="widgetsLoading" type="list-item-two-line@5" />
          <div v-else-if="!activityWidgets.upcoming.items.length" class="ncrm-widget-card__empty">
            <v-icon size="32" class="ncrm-widget-card__empty-icon">mdi-calendar-check-outline</v-icon>
            <span>{{ $t("ncrm.board.widgets.upcoming.empty", { hours: activityWidgets.upcoming.hours }) }}</span>
          </div>
          <div v-else class="ncrm-activity-list">
            <div
              v-for="item in activityWidgets.upcoming.items"
              :key="`upcoming-${item.id}`"
              class="ncrm-activity-row"
              @click="openSource(item)"
            >
              <div class="ncrm-activity-row__icon ncrm-activity-row__icon--warning">
                <v-icon size="13">{{ activityTypeIcon(item.activity_type) }}</v-icon>
              </div>
              <div class="ncrm-activity-row__body">
                <div class="ncrm-activity-row__title">{{ resolveActivityTitle(item) }}</div>
                <div class="ncrm-activity-row__meta">
                  <span>{{ activityTypeLabel(item.activity_type) }}</span>
                  <span class="ncrm-activity-row__sep">·</span>
                  <span class="ncrm-activity-row__date">{{ formatDateTime(item.due_at) }}</span>
                </div>
              </div>
              <span class="ncrm-activity-row__company">{{ resolveCompanyLabel(item) }}</span>
              <v-icon size="13" class="ncrm-activity-row__chevron">mdi-chevron-right</v-icon>
            </div>
          </div>
        </div>

        <div class="ncrm-widget-card__footer">
          <button class="ncrm-widget-card__footer-link" @click="openUpcomingList">
            <v-icon size="13" class="mr-1">mdi-open-in-new</v-icon>
            {{ $t("ncrm.board.widgets.upcoming.openList") }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Smart Tracker widget ───────────────────────────── -->
    <div v-if="activeWidgetKeys.includes('smartTracker')" class="ncrm-widget-card ncrm-widget-card--tracker">
      <div class="ncrm-widget-card__header">
        <div class="ncrm-widget-card__header-left">
          <v-icon size="16" color="primary" class="mr-1">mdi-timer-sand</v-icon>
          <span class="ncrm-widget-card__title">{{ $t("ncrm.board.widgets.smartTracker.title") }}</span>
        </div>
        <v-chip size="x-small" :color="smartTrackerStatusChip.color">
          {{ smartTrackerStatusChip.label }}
        </v-chip>
      </div>

      <div class="ncrm-widget-card__body">
        <v-skeleton-loader v-if="widgetsLoading" type="article" />
        <div v-else>
          <v-alert
            v-if="!smartTrackerSummary.settings.smart_tracker_enabled"
            type="info" density="compact" class="mb-4"
          >
            {{ $t("ncrm.board.widgets.smartTracker.disabled") }}
          </v-alert>

          <!-- Timers row -->
          <div class="ncrm-tracker__timers mb-4">
            <div class="ncrm-tracker__timer ncrm-tracker__timer--session">
              <span class="ncrm-tracker__timer-eyebrow">{{ $t("ncrm.board.widgets.smartTracker.sessionTimer") }}</span>
              <span class="ncrm-tracker__timer-value">{{ formatDuration(smartTrackerSessionSecondsDisplay) }}</span>
            </div>
            <div class="ncrm-tracker__timer ncrm-tracker__timer--day">
              <span class="ncrm-tracker__timer-eyebrow">{{ $t("ncrm.board.widgets.smartTracker.dayTimer") }}</span>
              <span class="ncrm-tracker__timer-value">{{ formatDuration(smartTrackerDaySecondsDisplay) }}</span>
            </div>
          </div>

          <!-- Label -->
          <div class="ncrm-tracker__label-section">
            <div class="ncrm-tracker__label-eyebrow">{{ $t("ncrm.board.widgets.smartTracker.label") }}</div>
            <v-textarea
              ref="inlineInputLabel"
              v-model="smartTrackerInlineLabel.value"
              :placeholder="$t('ncrm.board.widgets.smartTracker.emptyLabel')"
              :counter="250"
              :maxlength="250"
              auto-grow rows="2"
              hide-details="auto"
              :disabled="!smartTrackerEditableSession"
              @update:model-value="onSmartTrackerInlineLabelInput"
              @blur="onSmartTrackerInlineLabelBlur"
            />
            <div class="d-flex align-center ga-2 mt-1">
              <v-progress-circular v-if="smartTrackerInlineLabel.saving" size="12" width="2" color="primary" indeterminate />
              <span v-else class="text-caption text-medium-emphasis">{{ $t("ncrm.board.widgets.smartTracker.inlineAutosave") }}</span>
            </div>
          </div>

          <v-alert v-if="smartTrackerInlineLabel.error" type="error" density="compact" class="mt-2">
            {{ smartTrackerInlineLabel.error }}
          </v-alert>

          <!-- Top contexts -->
          <template v-if="smartTrackerTopContexts.length">
            <div class="ncrm-tracker__contexts-eyebrow mt-4">{{ $t("ncrm.board.widgets.smartTracker.topContexts") }}</div>
            <div class="ncrm-tracker__context-list mt-2">
              <div v-for="(ctx, i) in smartTrackerTopContexts" :key="ctx.key" class="ncrm-tracker__context-row">
                <span class="ncrm-tracker__context-name">{{ ctx.label }}</span>
                <div class="ncrm-tracker__context-track">
                  <div
                    class="ncrm-tracker__context-fill"
                    :class="i === 0 ? 'ncrm-tracker__context-fill--primary' : 'ncrm-tracker__context-fill--neutral'"
                    :style="{ width: Math.min(100, ctx.percent) + '%' }"
                  />
                </div>
                <span class="ncrm-tracker__context-pct">{{ formatPercent(ctx.percent) }}%</span>
              </div>
            </div>
          </template>
          <v-alert v-else type="info" density="compact" class="mt-3">
            {{ $t("ncrm.board.widgets.smartTracker.noContexts") }}
          </v-alert>
        </div>
      </div>
    </div>

    <NcrmBoardWeeklyTrend
      v-if="canAccessActivityWidgets"
      :loading="weeklyTrend.loading"
      :days="weeklyTrend.days"
    />

    <!-- ── Widget manager dialog ──────────────────────────── -->
    <v-dialog v-model="widgetManagerDialog" max-width="680">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold px-5 pt-5 pb-2">
          {{ $t("ncrm.board.widgets.manageTitle") }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="ncrm-manager-section-label mb-2">{{ $t("ncrm.board.widgets.activeTitle") }}</div>
              <v-alert v-if="!activeWidgetItems.length" type="info" density="compact">
                {{ $t("ncrm.board.widgets.emptyActive") }}
              </v-alert>
              <v-list v-else density="compact" class="ncrm-widget-manager__list">
                <v-list-item v-for="(item, index) in activeWidgetItems" :key="`active-${item.key}`" :title="item.label">
                  <template #append>
                    <div class="d-flex align-center ga-1">
                      <v-btn icon="mdi-arrow-up" size="x-small" variant="text" :disabled="index === 0" :aria-label="$t('ncrm.board.widgets.moveUp')" @click="moveWidget(item.key, -1)" />
                      <v-btn icon="mdi-arrow-down" size="x-small" variant="text" :disabled="index === activeWidgetItems.length - 1" :aria-label="$t('ncrm.board.widgets.moveDown')" @click="moveWidget(item.key, 1)" />
                      <v-btn icon="mdi-close" size="x-small" variant="text" color="error" :aria-label="$t('ncrm.board.widgets.remove')" @click="removeWidget(item.key)" />
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="6">
              <div class="ncrm-manager-section-label mb-2">{{ $t("ncrm.board.widgets.availableTitle") }}</div>
              <v-alert v-if="!availableWidgetItems.length" type="success" density="compact">
                {{ $t("ncrm.board.widgets.emptyAvailable") }}
              </v-alert>
              <v-list v-else density="compact" class="ncrm-widget-manager__list">
                <v-list-item v-for="item in availableWidgetItems" :key="`avail-${item.key}`" :title="item.label">
                  <template #append>
                    <v-btn icon="mdi-plus" size="x-small" variant="text" color="primary" :aria-label="$t('ncrm.board.widgets.add')" @click="addWidget(item.key)" />
                  </template>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3 justify-end">
          <v-btn variant="text" @click="widgetManagerDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import { normalizeNcrmActivityItem } from "@/Utile/ncrmActivity.mjs";
import { resolveNcrmActivitySourceRoute } from "@/Utile/ncrmRouting.mjs";
import { normalizeRoleKey, expandRoleAliases } from "@/Utile/roleHelpers.mjs";
import { getSmartTrackerRuntime } from "@/Utile/smartTrackerRuntime.mjs";
import { parseCurrentUserIdFromToken } from "@/Utile/ncrmCurrentUser.mjs";
import NcrmBoardModuleTiles from "./board/NcrmBoardModuleTiles.vue";
import NcrmBoardWeeklyTrend from "./board/NcrmBoardWeeklyTrend.vue";
import PageHelpButton from "@/components/tours/PageHelpButton.vue";
const BOARD_WIDGET_STORAGE_KEY = "ncrm.board.widgets.v2";
const BOARD_WIDGETS_CATALOG = Object.freeze([
  Object.freeze({
    key: "overdue",
    labelKey: "ncrm.board.widgets.catalog.overdue",
    layout: Object.freeze({ cols: 12, md: 6, lg: 6 }),
  }),
  Object.freeze({
    key: "upcoming",
    labelKey: "ncrm.board.widgets.catalog.upcoming",
    layout: Object.freeze({ cols: 12, md: 6, lg: 6 }),
  }),
  Object.freeze({
    key: "smartTracker",
    labelKey: "ncrm.board.widgets.catalog.smartTracker",
    layout: Object.freeze({ cols: 12, md: 12, lg: 12 }),
  }),
]);
const BOARD_WIDGET_KEYS = Object.freeze(BOARD_WIDGETS_CATALOG.map((item) => item.key));
const DEFAULT_ACTIVE_WIDGET_KEYS = Object.freeze([...BOARD_WIDGET_KEYS]);
const cloneDefaultWidgetKeys = () => [...DEFAULT_ACTIVE_WIDGET_KEYS];
const ACTIVITY_WIDGET_ROLES = Object.freeze(["admin", "miniadmin", "crm-admin", "crm-operator"]);
const SMART_TRACKER_WIDGET_KEY = "smartTracker";

const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseOptionalBoolean = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(normalized)) return true;
  if (["0", "false", "no", "n"].includes(normalized)) return false;
  return null;
};

const resolveSmartTrackerModuleKey = (routePath) => {
  const normalized = String(routePath || "").trim().toLowerCase();
  if (!normalized || normalized === "/") return "app-home";
  if (normalized.startsWith("/ncrm/leads")) return "crm-leads";
  if (normalized.startsWith("/ncrm/deals")) return "crm-deals";
  if (normalized.startsWith("/ncrm/contacts")) return "crm-contacts";
  if (normalized.startsWith("/ncrm/activities")) return "crm-activities";
  if (normalized.startsWith("/ncrm/data-integrations")) return "crm-integrations";
  if (normalized.startsWith("/ncrm")) return "crm-board";
  if (normalized.startsWith("/hr-dashboard")) return "hr-dashboard";
  if (normalized.startsWith("/dashboard")) return "employee-dashboard";
  const firstSegment = normalized.split("/").filter(Boolean)[0] || "app";
  return `app-${firstSegment}`;
};

export default {
  name: "NcrmBoard",
  components: {
    NcrmBoardModuleTiles,
    NcrmBoardWeeklyTrend,
    PageHelpButton,
  },
  data() {
    return {
      widgetsLoading: false,
      widgetManagerDialog: false,
      canAccessActivityWidgets: true,
      activeWidgetKeys: cloneDefaultWidgetKeys(),
      activityWidgets: {
        overdue: {
          count: 0,
          items: [],
        },
        upcoming: {
          count: 0,
          hours: 72,
          items: [],
        },
      },
      smartTrackerSummary: {
        date: null,
        session_timer_seconds: 0,
        today_total_seconds: 0,
        sessions_count: 0,
        sessions: [],
        settings: {
          use_automatic: null,
          smart_tracker_enabled: false,
          smart_tracker_idle_seconds: 180,
          smart_tracker_prompt_seconds: 30,
          smart_tracker_auto_label_enabled: true,
        },
      },
      smartTrackerLoadedAt: 0,
      smartTrackerTickerNow: Date.now(),
      smartTrackerRefreshTimer: null,
      smartTrackerTickTimer: null,
      smartTrackerRuntime: null,
      smartTrackerRuntimeUnwatch: null,
      smartTrackerMonotonic: {
        date: null,
        daySeconds: 0,
        activeSessionId: null,
        activeSessionSeconds: 0,
      },
      smartTrackerInlineLabel: {
        sessionId: null,
        value: "",
        lastSyncedValue: "",
        saving: false,
        error: "",
        debounceTimer: null,
      },
      hasPersistedWidgetConfig: false,
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
      weeklyTrend: {
        loading: false,
        days: [],
      },
      moduleStats: {
        activitiesMyTotal: null,
        activitiesMyOverdue: null,
        contactsCompanies: null,
        contactsPersons: null,
        leadsOpen: null,
        leadsOverdue: null,
        dealsOpen: null,
        dealsOverdue: null,
        contractsActive: null,
        contractsWithOverdue: null,
        onboardingActive: null,
        onboardingOverdue: null,
      },
    };
  },
  computed: {
    moduleNavItems() {
      const myOverdue = this.moduleStats.activitiesMyOverdue;
      return [
        {
          route: "/ncrm/activities",
          icon: "mdi-calendar-check-outline",
          label: this.$t("navigation.ncrmActivities"),
          color: "rgb(var(--v-theme-info))",
          alertColor: "rgb(var(--v-theme-error))",
          stats: [
            { icon: "mdi-calendar-check-outline", value: this.moduleStats.activitiesMyTotal, color: "default" },
            { icon: "mdi-alert-circle-outline", value: myOverdue, color: myOverdue > 0 ? "error" : "muted" },
          ],
        },
        {
          route: "/ncrm/contacts",
          icon: "mdi-account-multiple-outline",
          label: this.$t("navigation.ncrmContacts"),
          color: "rgb(var(--v-theme-tertiary))",
          alertColor: "rgb(var(--v-theme-warning))",
          stats: [
            { icon: "mdi-domain", value: this.moduleStats.contactsCompanies, color: "default" },
            { icon: "mdi-account-outline", value: this.moduleStats.contactsPersons, color: "default" },
          ],
        },
        {
          route: "/ncrm/leads",
          icon: "mdi-filter-multiple-outline",
          label: this.$t("navigation.ncrmLeads"),
          color: "rgb(var(--v-theme-primary))",
          alertColor: "rgb(var(--v-theme-error))",
          stats: [
            { icon: "mdi-filter-outline", value: this.moduleStats.leadsOpen, color: "default" },
            { icon: "mdi-clock-alert-outline", value: this.moduleStats.leadsOverdue, color: this.moduleStats.leadsOverdue > 0 ? "error" : "muted" },
          ],
        },
        {
          route: "/ncrm/deals",
          icon: "mdi-handshake-outline",
          label: this.$t("navigation.ncrmDeals"),
          color: "rgb(var(--v-theme-success))",
          alertColor: "rgb(var(--v-theme-error))",
          stats: [
            { icon: "mdi-handshake-outline", value: this.moduleStats.dealsOpen, color: "default" },
            { icon: "mdi-clock-alert-outline", value: this.moduleStats.dealsOverdue, color: this.moduleStats.dealsOverdue > 0 ? "error" : "muted" },
          ],
        },
        {
          route: "/ncrm/contracts",
          icon: "mdi-file-document-outline",
          label: this.$t("navigation.ncrmContracts"),
          color: "rgb(var(--v-theme-secondary))",
          alertColor: "rgb(var(--v-theme-warning))",
          stats: [
            { icon: "mdi-file-document-check-outline", value: this.moduleStats.contractsActive, color: "default" },
            { icon: "mdi-alert-outline", value: this.moduleStats.contractsWithOverdue, color: this.moduleStats.contractsWithOverdue > 0 ? "error" : "muted" },
          ],
        },
        {
          route: "/ncrm/onboarding",
          icon: "mdi-rocket-launch-outline",
          label: this.$t("navigation.ncrmOnboarding"),
          color: "rgb(var(--v-theme-quaternary))",
          alertColor: "rgb(var(--v-theme-error))",
          stats: [
            { icon: "mdi-rocket-launch-outline", value: this.moduleStats.onboardingActive, color: "default" },
            { icon: "mdi-clock-alert-outline", value: this.moduleStats.onboardingOverdue, color: this.moduleStats.onboardingOverdue > 0 ? "error" : "muted" },
          ],
        },
      ];
    },
    widgetCatalog() {
      const allowed = new Set(this.getAllowedWidgetKeys());
      return BOARD_WIDGETS_CATALOG.filter((item) => allowed.has(item.key)).map((item) => ({
        key: item.key,
        label: this.$t(item.labelKey),
        layout: item.layout,
      }));
    },
    activeWidgetItems() {
      const activeKeys = this.activeWidgetKeys.filter((key) => BOARD_WIDGET_KEYS.includes(key));
      return activeKeys
        .map((key) => this.widgetCatalog.find((item) => item.key === key))
        .filter(Boolean);
    },
    availableWidgetItems() {
      const activeSet = new Set(this.activeWidgetItems.map((item) => item.key));
      return this.widgetCatalog.filter((item) => !activeSet.has(item.key));
    },
    smartTrackerActiveSession() {
      const sessions = Array.isArray(this.smartTrackerSummary?.sessions) ? this.smartTrackerSummary.sessions : [];
      const active = sessions.find((item) => String(item?.state || "").toLowerCase() === "active");
      if (active) return active;
      return sessions[0] || null;
    },
    smartTrackerEditableSession() {
      return this.smartTrackerActiveSession;
    },
    smartTrackerStatusKey() {
      const enabled = Boolean(this.smartTrackerSummary?.settings?.smart_tracker_enabled);
      if (!enabled) return "disabled";
      const state = String(this.smartTrackerActiveSession?.state || "").toLowerCase();
      if (state === "active") return "active";
      if (state === "paused") return "paused";
      return "stopped";
    },
    smartTrackerStatusChip() {
      const key = this.smartTrackerStatusKey;
      const map = {
        active: { color: "success", label: this.$t("ncrm.board.widgets.smartTracker.status.active") },
        paused: { color: "warning", label: this.$t("ncrm.board.widgets.smartTracker.status.paused") },
        stopped: { color: "neutral", label: this.$t("ncrm.board.widgets.smartTracker.status.stopped") },
        disabled: { color: "neutral", label: this.$t("ncrm.board.widgets.smartTracker.status.disabled") },
      };
      return map[key] || map.stopped;
    },
    smartTrackerSessionSecondsDisplay() {
      const base = parseNumeric(this.smartTrackerSummary?.session_timer_seconds, 0);
      if (this.smartTrackerStatusKey !== "active") return base;
      if (!this.smartTrackerLoadedAt) return base;
      const delta = Math.max(0, Math.floor((this.smartTrackerTickerNow - this.smartTrackerLoadedAt) / 1000));
      return base + delta;
    },
    smartTrackerDaySecondsDisplay() {
      const base = parseNumeric(this.smartTrackerSummary?.today_total_seconds, 0);
      if (this.smartTrackerStatusKey !== "active") return base;
      if (!this.smartTrackerLoadedAt) return base;
      const delta = Math.max(0, Math.floor((this.smartTrackerTickerNow - this.smartTrackerLoadedAt) / 1000));
      return base + delta;
    },
    smartTrackerTopContexts() {
      const session = this.smartTrackerActiveSession;
      if (!session) return [];
      const entries = this.parseContextSummary(session);
      return entries.slice(0, 3);
    },
  },
  mounted() {
    this.smartTrackerRuntime = getSmartTrackerRuntime();
    this.smartTrackerRuntimeUnwatch = this.$watch(
      () => {
        const runtimeState = this.smartTrackerRuntime?.state;
        if (!runtimeState) return "";
        return [
          runtimeState.routePath || "",
          runtimeState.sessionState || "stopped",
          Number(runtimeState.sessionId || 0),
          Number(runtimeState.enabled ? 1 : 0),
        ].join("|");
      },
      () => {
        this.loadSmartTrackerSummary({ silent: true });
      }
    );
    this.hydrateRoleCapabilities();
    this.restoreWidgetConfig();
    this.loadBoardWidgets();
    this.smartTrackerRefreshTimer = setInterval(() => {
      this.loadSmartTrackerSummary({ silent: true });
    }, 20_000);
    this.smartTrackerTickTimer = setInterval(() => {
      this.smartTrackerTickerNow = Date.now();
    }, 1_000);
  },
  beforeUnmount() {
    if (typeof this.smartTrackerRuntimeUnwatch === "function") {
      this.smartTrackerRuntimeUnwatch();
      this.smartTrackerRuntimeUnwatch = null;
    }
    this.clearSmartTrackerInlineSaveTimer();
    if (this.smartTrackerRefreshTimer) {
      clearInterval(this.smartTrackerRefreshTimer);
      this.smartTrackerRefreshTimer = null;
    }
    if (this.smartTrackerTickTimer) {
      clearInterval(this.smartTrackerTickTimer);
      this.smartTrackerTickTimer = null;
    }
  },
  methods: {
    getAllowedWidgetKeys() {
      const allowed = [];
      if (this.canAccessActivityWidgets) {
        allowed.push(...BOARD_WIDGET_KEYS.filter((key) => key !== SMART_TRACKER_WIDGET_KEY));
      }
      // Keep Smart Tracker widget available in board manager even when disabled/loading.
      allowed.push(SMART_TRACKER_WIDGET_KEY);
      return allowed;
    },
    sanitizeActiveWidgetKeys(nextKeys = this.activeWidgetKeys) {
      const allowed = new Set(this.getAllowedWidgetKeys());
      const normalized = (Array.isArray(nextKeys) ? nextKeys : [])
        .map((item) => String(item || "").trim())
        .filter((item, index, source) => item && source.indexOf(item) === index)
        .filter((item) => allowed.has(item));
      const fallbackKeys = DEFAULT_ACTIVE_WIDGET_KEYS.filter((item) => allowed.has(item));
      this.activeWidgetKeys = normalized.length ? normalized : fallbackKeys;
    },
    syncActiveWidgetsWithCapabilities({ markAsPersisted = false } = {}) {
      const previous = JSON.stringify(this.activeWidgetKeys || []);
      this.sanitizeActiveWidgetKeys(this.activeWidgetKeys);
      const current = JSON.stringify(this.activeWidgetKeys || []);
      if (previous !== current) {
        this.persistWidgetConfig({ markAsPersisted });
      }
    },
    hydrateRoleCapabilities() {
      if (typeof window === "undefined") {
        this.canAccessActivityWidgets = true;
        this.sanitizeActiveWidgetKeys();
        return;
      }

      const roleSet = new Set();
      const addRole = (roleValue) => {
        const normalized = normalizeRoleKey(roleValue);
        if (!normalized) return;
        expandRoleAliases(normalized).forEach((alias) => roleSet.add(alias));
      };

      addRole(window.localStorage.getItem("user-role"));
      addRole(window.localStorage.getItem("user-primary-role"));

      try {
        const raw = JSON.parse(window.localStorage.getItem("user-roles") || "[]");
        if (Array.isArray(raw)) {
          raw.forEach((entry) => {
            if (!entry) return;
            if (typeof entry === "string") {
              addRole(entry);
              return;
            }
            addRole(entry.key || entry.role || entry.name);
          });
        }
      } catch (_error) {
        // ignore parse failures and fallback to legacy role keys
      }

      this.canAccessActivityWidgets = ACTIVITY_WIDGET_ROLES.some((role) => roleSet.has(role));
      this.syncActiveWidgetsWithCapabilities();
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    persistWidgetConfig({ markAsPersisted = true } = {}) {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(
          BOARD_WIDGET_STORAGE_KEY,
          JSON.stringify({ active_keys: this.activeWidgetKeys })
        );
        if (markAsPersisted) {
          this.hasPersistedWidgetConfig = true;
        }
      } catch (_) {
        // ignore storage failures
      }
    },
    restoreWidgetConfig() {
      if (typeof window === "undefined") return;
      try {
        const raw = window.localStorage.getItem(BOARD_WIDGET_STORAGE_KEY);
        if (!raw) {
          this.hasPersistedWidgetConfig = false;
          this.sanitizeActiveWidgetKeys();
          return;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {
          this.hasPersistedWidgetConfig = false;
          this.sanitizeActiveWidgetKeys();
          return;
        }
        const activeKeys = Array.isArray(parsed?.active_keys)
          ? parsed.active_keys.map((item) => String(item || "").trim())
          : [];
        this.hasPersistedWidgetConfig = true;
        this.sanitizeActiveWidgetKeys(activeKeys);
      } catch (_) {
        this.hasPersistedWidgetConfig = false;
        this.activeWidgetKeys = cloneDefaultWidgetKeys();
        this.sanitizeActiveWidgetKeys();
      }
    },
    addWidget(key) {
      const normalized = String(key || "").trim();
      const allowed = new Set(this.getAllowedWidgetKeys());
      if (!allowed.has(normalized)) return;
      if (this.activeWidgetKeys.includes(normalized)) return;
      this.activeWidgetKeys = [...this.activeWidgetKeys, normalized];
      this.persistWidgetConfig();
    },
    removeWidget(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return;
      this.sanitizeActiveWidgetKeys(this.activeWidgetKeys.filter((item) => item !== normalized));
      this.persistWidgetConfig();
    },
    moveWidget(key, direction) {
      const normalized = String(key || "").trim();
      const delta = Number(direction || 0);
      const currentIndex = this.activeWidgetKeys.findIndex((item) => item === normalized);
      if (currentIndex === -1) return;
      const targetIndex = currentIndex + (delta < 0 ? -1 : 1);
      if (targetIndex < 0 || targetIndex >= this.activeWidgetKeys.length) return;
      const next = [...this.activeWidgetKeys];
      const [item] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, item);
      this.activeWidgetKeys = next;
      this.persistWidgetConfig();
    },
    activityTypeIcon(type) {
      const map = {
        call: "mdi-phone-outline",
        email: "mdi-email-outline",
        meeting: "mdi-account-group-outline",
        todo: "mdi-checkbox-marked-circle-outline",
        task: "mdi-checkbox-marked-circle-outline",
        note: "mdi-note-text-outline",
      };
      return map[String(type || "").toLowerCase()] || "mdi-calendar-check-outline";
    },
    resolveCompanyLabel(item) {
      return String(item?.entity_label || item?.company_name || item?.lead_name || "").trim();
    },
    formatDateTime(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      const day = String(parsed.getDate()).padStart(2, "0");
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const year = parsed.getFullYear();
      const hours = String(parsed.getHours()).padStart(2, "0");
      const minutes = String(parsed.getMinutes()).padStart(2, "0");
      return `${day}.${month}.${year}, ${hours}:${minutes}`;
    },
    formatDuration(value) {
      const total = Math.max(0, parseNumeric(value, 0));
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    },
    formatPercent(value) {
      return parseNumeric(value, 0).toFixed(1);
    },
    resolveActivityTitle(item) {
      const title = String(item?.title || "").trim();
      if (title) return title;
      const content = String(item?.content || "").trim();
      if (content) {
        return content.length > 72 ? `${content.slice(0, 72)}...` : content;
      }
      return this.activityTypeLabel(item?.activity_type);
    },
    activityTypeLabel(type) {
      const normalized = String(type || "").trim().toLowerCase();
      return this.$t(`ncrm.activity.types.${normalized}`) || normalized || "-";
    },
    parseContextSummary(session) {
      const contextSummary = session?.context_summary_json;
      let parsed = contextSummary;
      if (typeof contextSummary === "string") {
        try {
          parsed = JSON.parse(contextSummary);
        } catch (_) {
          parsed = null;
        }
      }
      if (!parsed || typeof parsed !== "object") return [];

      const totalSeconds = Math.max(0, parseNumeric(parsed?.total_seconds, 0));
      const normalizeStructuredEntry = (entry) => {
        const key = String(entry?.key || entry?.context_key || entry?.label || "").trim();
        if (!key) return null;
        const seconds = Math.max(0, parseNumeric(entry?.seconds ?? entry?.total_seconds, 0));
        let percent = parseNumeric(entry?.percent, Number.NaN);
        if (!Number.isFinite(percent) || percent < 0) {
          percent = totalSeconds > 0 ? Number(((seconds / totalSeconds) * 100).toFixed(2)) : 0;
        }
        return {
          key,
          label: key,
          percent: Math.max(0, percent),
          seconds,
        };
      };

      if (Array.isArray(parsed?.contexts)) {
        const contextMap = new Map();
        parsed.contexts
          .map((item) => normalizeStructuredEntry(item))
          .filter(Boolean)
          .forEach((item) => {
            const previous = contextMap.get(item.key);
            if (!previous || item.percent > previous.percent) {
              contextMap.set(item.key, item);
            }
          });

        const selectedKeys = Array.isArray(parsed?.selected_contexts)
          ? parsed.selected_contexts.map((item) => String(item || "").trim()).filter(Boolean)
          : [];

        const selectedEntries = selectedKeys.map((key) => contextMap.get(key)).filter(Boolean);
        const selectedSet = new Set(selectedEntries.map((item) => item.key));
        const remainingEntries = Array.from(contextMap.values())
          .filter((item) => !selectedSet.has(item.key))
          .sort(
            (left, right) =>
              right.percent - left.percent || right.seconds - left.seconds || left.key.localeCompare(right.key)
          );

        return [...selectedEntries, ...remainingEntries];
      }

      const metadataKeys = new Set(["total_seconds", "threshold_percent", "contexts", "selected_contexts"]);
      const entries = Object.entries(parsed)
        .map(([key, rawValue]) => {
          if (metadataKeys.has(String(key || "").trim())) return null;
          const label = String(key || "").trim();
          if (!label) return null;
          const percent = parseNumeric(rawValue, Number.NaN);
          if (!Number.isFinite(percent)) return null;
          return {
            key: label,
            label,
            percent: Math.max(0, percent),
            seconds: 0,
          };
        })
        .filter(Boolean)
        .sort((left, right) => right.percent - left.percent || left.key.localeCompare(right.key));

      return entries;
    },
    resolveSmartTrackerRoutePath() {
      if (this.$route?.path) return this.$route.path;
      if (typeof window !== "undefined" && window.location?.pathname) {
        return window.location.pathname;
      }
      return "/ncrm";
    },
    buildSmartTrackerContextPayload() {
      const routePath = this.resolveSmartTrackerRoutePath();
      const moduleKey = resolveSmartTrackerModuleKey(routePath);
      return {
        occurred_at: new Date().toISOString(),
        module_key: moduleKey,
        route_path: routePath,
        context_key: moduleKey,
        context_label: moduleKey,
      };
    },
    resolveActiveSessionRuntimeSeconds(sessions = []) {
      const activeSession = Array.isArray(sessions)
        ? sessions.find((item) => String(item?.state || "").toLowerCase() === "active")
        : null;
      if (!activeSession) {
        return {
          id: null,
          seconds: 0,
        };
      }
      const sessionId = parseNumeric(activeSession?.id, 0);
      const runtimeSeconds = parseNumeric(activeSession?.runtime_seconds, parseNumeric(activeSession?.duration_seconds, 0));
      return {
        id: sessionId > 0 ? sessionId : null,
        seconds: Math.max(0, runtimeSeconds),
      };
    },
    applySmartTrackerMonotonicGuard(summary = {}) {
      const normalizedDate = String(summary?.date || "").trim() || null;
      const sessions = Array.isArray(summary?.sessions) ? summary.sessions : [];
      const activeRuntime = this.resolveActiveSessionRuntimeSeconds(sessions);

      let nextDaySeconds = Math.max(0, parseNumeric(summary?.today_total_seconds, 0));
      if (
        normalizedDate &&
        normalizedDate === this.smartTrackerMonotonic.date &&
        nextDaySeconds < parseNumeric(this.smartTrackerMonotonic.daySeconds, 0)
      ) {
        nextDaySeconds = parseNumeric(this.smartTrackerMonotonic.daySeconds, 0);
      }

      let nextSessionSeconds = Math.max(0, parseNumeric(summary?.session_timer_seconds, 0));
      if (activeRuntime.id && activeRuntime.seconds > nextSessionSeconds) {
        nextSessionSeconds = activeRuntime.seconds;
      }
      if (
        activeRuntime.id &&
        activeRuntime.id === parseNumeric(this.smartTrackerMonotonic.activeSessionId, 0) &&
        nextSessionSeconds < parseNumeric(this.smartTrackerMonotonic.activeSessionSeconds, 0)
      ) {
        nextSessionSeconds = parseNumeric(this.smartTrackerMonotonic.activeSessionSeconds, 0);
      }

      this.smartTrackerMonotonic = {
        date: normalizedDate,
        daySeconds: nextDaySeconds,
        activeSessionId: activeRuntime.id,
        activeSessionSeconds: activeRuntime.id ? nextSessionSeconds : 0,
      };

      return {
        ...summary,
        today_total_seconds: nextDaySeconds,
        session_timer_seconds: nextSessionSeconds,
      };
    },
    async loadActivityWidgets() {
      const { data } = await axios.get("/ncrm/activity/widgets/summary", {
        params: { limit: 6, upcomingHours: 72 },
      });
      const overdueItems = Array.isArray(data?.overdue?.items)
        ? data.overdue.items.map((item) => normalizeNcrmActivityItem(item))
        : [];
      const upcomingItems = Array.isArray(data?.upcoming?.items)
        ? data.upcoming.items.map((item) => normalizeNcrmActivityItem(item))
        : [];
      this.activityWidgets = {
        overdue: {
          count: Number(data?.overdue?.count || 0),
          items: overdueItems,
        },
        upcoming: {
          count: Number(data?.upcoming?.count || 0),
          hours: Number(data?.upcoming?.hours || 72),
          items: upcomingItems,
        },
      };
    },
    async loadSmartTrackerSummary({ silent = false, ensureSession = false } = {}) {
      try {
        if (ensureSession) {
          try {
            await axios.post("/smart-tracker/session/start", this.buildSmartTrackerContextPayload());
          } catch (startError) {
            const startCode = Number(startError?.response?.status || 0);
            if (startCode !== 409 && startCode !== 423) {
              // Start can fail transiently; we still refresh summary from backend.
              console.error("[ncrm-board] smart tracker start failed before summary load:", startError);
            }
          }
        }

        const { data } = await axios.get("/smart-tracker/me/today", {
          params: {
            _ts: Date.now(),
          },
        });
        const nextSummary = {
          date: data?.date || null,
          session_timer_seconds: parseNumeric(data?.session_timer_seconds, 0),
          today_total_seconds: parseNumeric(data?.today_total_seconds, 0),
          sessions_count: parseNumeric(data?.sessions_count, 0),
          sessions: Array.isArray(data?.sessions) ? data.sessions : [],
          settings: {
            use_automatic: parseOptionalBoolean(data?.settings?.use_automatic),
            smart_tracker_enabled: Boolean(data?.settings?.smart_tracker_enabled),
            smart_tracker_idle_seconds: parseNumeric(data?.settings?.smart_tracker_idle_seconds, 180),
            smart_tracker_prompt_seconds: parseNumeric(data?.settings?.smart_tracker_prompt_seconds, 30),
            smart_tracker_auto_label_enabled:
              data?.settings?.smart_tracker_auto_label_enabled === null ||
              data?.settings?.smart_tracker_auto_label_enabled === undefined
                ? true
                : Boolean(data.settings.smart_tracker_auto_label_enabled),
          },
        };
        this.smartTrackerSummary = this.applySmartTrackerMonotonicGuard(nextSummary);
        this.smartTrackerLoadedAt = Date.now();
        this.syncSmartTrackerInlineLabelFromSummary();
        this.syncActiveWidgetsWithCapabilities();
        this.syncDefaultSmartTrackerWidgetWithTimesheetSetting();
      } catch (error) {
        console.error("[ncrm-board] load smart tracker summary failed:", error);
        if (!silent) {
          const message = String(
            error?.response?.data?.error || this.$t("ncrm.board.widgets.smartTracker.loadError")
          ).trim();
          this.showSnackbar(message, "warning");
        }
      }
    },
    resolveAutomaticTimesheetSetting() {
      return parseOptionalBoolean(this.smartTrackerSummary?.settings?.use_automatic);
    },
    syncDefaultSmartTrackerWidgetWithTimesheetSetting() {
      if (this.hasPersistedWidgetConfig) return;
      const automaticTimesheetEnabled = this.resolveAutomaticTimesheetSetting();
      if (automaticTimesheetEnabled === null) return;
      const defaultKeys = automaticTimesheetEnabled
        ? cloneDefaultWidgetKeys()
        : cloneDefaultWidgetKeys().filter((key) => key !== SMART_TRACKER_WIDGET_KEY);
      this.sanitizeActiveWidgetKeys(defaultKeys);
    },
    async loadModuleStats() {
      try {
        const currentUserId = parseCurrentUserIdFromToken();
        const activityUserParams = currentUserId ? { owner_user_id: currentUserId } : {};

        const [
          activitiesMyTotal,
          activitiesMyOverdue,
          contactsCompanies,
          contactsPersons,
          leadsSummary,
          dealsSummary,
          contractsActive,
          onboardingActive,
          onboardingOverdue,
        ] = await Promise.allSettled([
          axios.get("/ncrm/activity/", { params: { ...activityUserParams, limit: 1 } }),
          axios.get("/ncrm/activity/", { params: { ...activityUserParams, overdue: true, limit: 1 } }),
          axios.get("/ncrm/contacts/", { params: { type: "company", limit: 1 } }),
          axios.get("/ncrm/contacts/", { params: { type: "person", limit: 1 } }),
          axios.get("/ncrm/leads/home/summary"),
          axios.get("/ncrm/deals/home/summary"),
          axios.get("/ncrm/contracts/", { params: { status: "active", limit: 200 } }),
          axios.get("/ncrm/onboarding/boards/entries", { params: { status: "active", limit: 1 } }),
          axios.get("/ncrm/onboarding/boards/entries", { params: { kpi: "overdue_tasks", limit: 1 } }),
        ]);

        const toStat = (raw) => {
          const n = Number(raw);
          return Number.isFinite(n) ? n : null;
        };

        let contractsWithOverdueCount = null;
        if (contractsActive.status === "fulfilled") {
          const items = Array.isArray(contractsActive.value.data?.items)
            ? contractsActive.value.data.items
            : [];
          contractsWithOverdueCount = items.filter((item) => Number(item.overdue_payment_count) > 0).length;
        }

        this.moduleStats = {
          activitiesMyTotal: activitiesMyTotal.status === "fulfilled" ? toStat(activitiesMyTotal.value.data?.total) : null,
          activitiesMyOverdue: activitiesMyOverdue.status === "fulfilled" ? toStat(activitiesMyOverdue.value.data?.total) : null,
          contactsCompanies: contactsCompanies.status === "fulfilled" ? toStat(contactsCompanies.value.data?.total) : null,
          contactsPersons: contactsPersons.status === "fulfilled" ? toStat(contactsPersons.value.data?.total) : null,
          leadsOpen: leadsSummary.status === "fulfilled" ? toStat(leadsSummary.value.data?.totals?.open_entries) : null,
          leadsOverdue: leadsSummary.status === "fulfilled" ? toStat(leadsSummary.value.data?.totals?.overdue_followups) : null,
          dealsOpen: dealsSummary.status === "fulfilled" ? toStat(dealsSummary.value.data?.totals?.open_deals) : null,
          dealsOverdue: dealsSummary.status === "fulfilled" ? toStat(dealsSummary.value.data?.totals?.overdue_followups) : null,
          contractsActive: contractsActive.status === "fulfilled" ? toStat(contractsActive.value.data?.total) : null,
          contractsWithOverdue: contractsWithOverdueCount,
          onboardingActive: onboardingActive.status === "fulfilled" ? toStat(onboardingActive.value.data?.total) : null,
          onboardingOverdue: onboardingOverdue.status === "fulfilled" ? toStat(onboardingOverdue.value.data?.total) : null,
        };
      } catch (error) {
        console.error("[ncrm-board] load module stats failed:", error);
      }
    },
    async loadBoardWidgets() {
      this.widgetsLoading = true;
      try {
        if (!this.canAccessActivityWidgets) {
          this.activityWidgets = {
            overdue: { count: 0, items: [] },
            upcoming: { count: 0, hours: 72, items: [] },
          };
          this.weeklyTrend.days = [];
          this.weeklyTrend.loading = false;
        }
        const requests = [
          this.loadSmartTrackerSummary({ silent: true, ensureSession: true }),
          this.loadModuleStats(),
        ];
        if (this.canAccessActivityWidgets) {
          requests.unshift(this.loadActivityWidgets());
          requests.push(this.loadWeeklyTrend());
        }
        const results = await Promise.allSettled(requests);
        const hasError = results.some((item) => item.status === "rejected");
        if (hasError) {
          this.showSnackbar(this.$t("ncrm.board.widgets.partialLoadError"), "warning");
        }
      } catch (error) {
        console.error("[ncrm-board] load widgets failed:", error);
        this.showSnackbar(this.$t("ncrm.board.widgets.loadError"), "error");
      } finally {
        this.widgetsLoading = false;
      }
    },
    openSource(item) {
      const targetRoute = resolveNcrmActivitySourceRoute(item);
      if (!targetRoute) {
        this.$router.push("/ncrm/activities");
        return;
      }
      this.$router.push(targetRoute);
    },
    goToModule(route) {
      this.$router.push(route);
    },
    async loadWeeklyTrend() {
      this.weeklyTrend.loading = true;
      try {
        const { data } = await axios.get("/ncrm/activity/widgets/weekly-trend");
        this.weeklyTrend.days = Array.isArray(data?.days) ? data.days : [];
      } catch (error) {
        console.error("[ncrm-board] load weekly trend failed:", error);
        this.weeklyTrend.days = [];
      } finally {
        this.weeklyTrend.loading = false;
      }
    },
    openOverdueList() {
      this.$router.push({ path: "/ncrm/activities", query: { overdue: "true" } });
    },
    openUpcomingList() {
      this.$router.push({ path: "/ncrm/activities", query: { overdue: "false" } });
    },
    syncSmartTrackerInlineLabelFromSummary() {
      const session = this.smartTrackerEditableSession;
      if (!session) {
        this.clearSmartTrackerInlineSaveTimer();
        this.smartTrackerInlineLabel = {
          ...this.smartTrackerInlineLabel,
          sessionId: null,
          value: "",
          lastSyncedValue: "",
          saving: false,
          error: "",
        };
        return;
      }

      const sessionId = Number(session?.id || 0) || null;
      const manual = String(session?.manual_label || "").trim();
      const auto = String(session?.auto_label || "").trim();
      const nextValue = manual || auto || "";
      const isSameSession = this.smartTrackerInlineLabel.sessionId === sessionId;
      const currentValue = String(this.smartTrackerInlineLabel.value || "").trim();
      const lastSynced = String(this.smartTrackerInlineLabel.lastSyncedValue || "").trim();
      const isDirty = currentValue !== lastSynced;

      if (!isSameSession || !isDirty) {
        this.smartTrackerInlineLabel = {
          ...this.smartTrackerInlineLabel,
          sessionId,
          value: nextValue,
          lastSyncedValue: nextValue,
          error: "",
        };
        return;
      }

      this.smartTrackerInlineLabel = {
        ...this.smartTrackerInlineLabel,
        sessionId,
      };
    },
    clearSmartTrackerInlineSaveTimer() {
      if (!this.smartTrackerInlineLabel.debounceTimer) return;
      clearTimeout(this.smartTrackerInlineLabel.debounceTimer);
      this.smartTrackerInlineLabel = {
        ...this.smartTrackerInlineLabel,
        debounceTimer: null,
      };
    },
    onSmartTrackerInlineLabelInput() {
      this.smartTrackerInlineLabel = {
        ...this.smartTrackerInlineLabel,
        error: "",
      };
      this.scheduleSmartTrackerInlineLabelSave();
    },
    onSmartTrackerInlineLabelBlur() {
      this.clearSmartTrackerInlineSaveTimer();
      this.saveSmartTrackerInlineLabel();
    },
    scheduleSmartTrackerInlineLabelSave() {
      if (!this.smartTrackerInlineLabel.sessionId) return;
      this.clearSmartTrackerInlineSaveTimer();
      const timerId = setTimeout(() => {
        this.saveSmartTrackerInlineLabel();
      }, 650);
      this.smartTrackerInlineLabel = {
        ...this.smartTrackerInlineLabel,
        debounceTimer: timerId,
      };
    },
    updateSmartTrackerSessionManualLabel(sessionId, manualLabel) {
      const normalizedSessionId = Number(sessionId || 0);
      if (!normalizedSessionId) return;
      const sessions = Array.isArray(this.smartTrackerSummary?.sessions) ? this.smartTrackerSummary.sessions : [];
      const updatedSessions = sessions.map((session) => {
        if (Number(session?.id || 0) !== normalizedSessionId) return session;
        return {
          ...session,
          manual_label: manualLabel,
        };
      });
      this.smartTrackerSummary = {
        ...this.smartTrackerSummary,
        sessions: updatedSessions,
      };
    },
    async saveSmartTrackerInlineLabel() {
      const sessionId = Number(this.smartTrackerInlineLabel.sessionId || 0);
      if (!sessionId) return;
      if (this.smartTrackerInlineLabel.saving) {
        return;
      }

      const raw = String(this.smartTrackerInlineLabel.value || "");
      const trimmed = raw.trim();
      if (trimmed.length > 250) {
        this.smartTrackerInlineLabel = {
          ...this.smartTrackerInlineLabel,
          error: this.$t("ncrm.board.widgets.smartTracker.editDialog.maxLength"),
        };
        return;
      }

      const lastSynced = String(this.smartTrackerInlineLabel.lastSyncedValue || "").trim();
      if (trimmed === lastSynced) return;

      this.smartTrackerInlineLabel = {
        ...this.smartTrackerInlineLabel,
        saving: true,
        error: "",
        debounceTimer: null,
      };
      try {
        await axios.patch(`/smart-tracker/sessions/${sessionId}/label`, {
          manual_label: trimmed || null,
        });
        this.smartTrackerInlineLabel = {
          ...this.smartTrackerInlineLabel,
          lastSyncedValue: trimmed,
          saving: false,
          error: "",
        };
        this.updateSmartTrackerSessionManualLabel(sessionId, trimmed || null);
      } catch (error) {
        const message = String(
          error?.response?.data?.error || this.$t("ncrm.board.widgets.smartTracker.editDialog.saveError")
        ).trim();
        this.smartTrackerInlineLabel = {
          ...this.smartTrackerInlineLabel,
          saving: false,
          error: message,
        };
        this.showSnackbar(message, "error");
      }

      const latestTrimmed = String(this.smartTrackerInlineLabel.value || "").trim();
      const latestSynced = String(this.smartTrackerInlineLabel.lastSyncedValue || "").trim();
      if (latestTrimmed !== latestSynced) {
        this.scheduleSmartTrackerInlineLabelSave();
      }
    },
  },
};
</script>

<style scoped>
/* ── Board container ──────────────────────────────────────── */
.ncrm-board {
  max-width: 1440px;
  margin: 0 auto;
  padding: 20px 24px 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}

/* ── Breadcrumb strip ─────────────────────────────────────── */
.ncrm-board__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 2px 0 10px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

.ncrm-board__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 0;
}

.ncrm-board__header-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-tertiary));
  opacity: 0.85;
}

.ncrm-board__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--app-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-board__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.6;
  white-space: nowrap;
  padding-left: 8px;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  margin-left: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-board__header-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ── KPI strip ────────────────────────────────────────────── */
.ncrm-board__kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.ncrm-kpi-tile {
  --ncrm-kpi-bg: rgb(var(--v-theme-primary));
  --ncrm-kpi-text: rgb(var(--v-theme-on-primary));
  --ncrm-kpi-shadow: rgba(var(--v-theme-primary), 0.4);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 18px 20px 14px;
  border: none;
  border-radius: var(--app-radius-base);
  cursor: default;
  text-align: left;
  position: relative;
  overflow: hidden;
  background: var(--ncrm-kpi-bg);
  color: var(--ncrm-kpi-text);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

button.ncrm-kpi-tile {
  cursor: pointer;
}

button.ncrm-kpi-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px var(--ncrm-kpi-shadow);
}

.ncrm-kpi-tile--error {
  --ncrm-kpi-bg: rgb(var(--v-theme-error));
  --ncrm-kpi-text: rgb(var(--v-theme-on-error));
  --ncrm-kpi-shadow: rgba(var(--v-theme-error), 0.4);
}

.ncrm-kpi-tile--warning {
  --ncrm-kpi-bg: rgb(var(--v-theme-warning));
  --ncrm-kpi-text: rgb(var(--v-theme-on-warning));
  --ncrm-kpi-shadow: rgba(var(--v-theme-warning), 0.4);
}

.ncrm-kpi-tile--neutral {
  --ncrm-kpi-bg: rgb(var(--v-theme-primary));
  --ncrm-kpi-text: rgb(var(--v-theme-on-primary));
  --ncrm-kpi-shadow: rgba(var(--v-theme-primary), 0.4);
}

.ncrm-kpi-tile--teal {
  --ncrm-kpi-bg: var(--app-tertiary-solid-bg);
  --ncrm-kpi-text: var(--app-tertiary-solid-text);
  --ncrm-kpi-shadow: rgba(var(--v-theme-tertiary), 0.4);
}

.ncrm-kpi-tile__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}

.ncrm-kpi-tile__eyebrow {
  font-size: 0.73rem;
  font-weight: 600;
  opacity: 0.75;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-kpi-tile__head-icon {
  opacity: 0.55;
  color: inherit !important;
  flex-shrink: 0;
}

.ncrm-kpi-tile__value {
  font-size: 2.15rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  margin: 4px 0;
}

.ncrm-kpi-tile__value--mono {
  font-family: var(--app-font-family-mono);
  font-size: 1.55rem;
  letter-spacing: 0.03em;
}

.ncrm-kpi-tile__foot {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 6px;
}

.ncrm-kpi-sparkline {
  width: 72px;
  height: 26px;
  display: block;
  overflow: visible;
  flex-shrink: 0;
}

/* ── Activity widgets grid ────────────────────────────────── */
.ncrm-board__activities {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

/* ── Widget card ──────────────────────────────────────────── */
.ncrm-widget-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
  background: var(--app-content-background);
  box-shadow: var(--app-shadow-sm);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ncrm-widget-card--tracker {
  grid-column: 1 / -1;
}

.ncrm-widget-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

.ncrm-widget-card__header-left {
  display: flex;
  align-items: center;
}

.ncrm-widget-card__title {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--app-text-strong);
}

.ncrm-widget-card__body {
  flex: 1;
  padding: 8px 12px;
  min-height: 180px;
}

.ncrm-widget-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 160px;
  color: var(--app-text-default);
  opacity: 0.6;
  font-size: 0.88rem;
  text-align: center;
}

.ncrm-widget-card__empty-icon {
  opacity: 0.4;
}

.ncrm-widget-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.ncrm-widget-card__footer-link {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: rgb(var(--v-theme-tertiary));
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.12s ease;
}

.ncrm-widget-card__footer-link:hover {
  opacity: 1;
}

.ncrm-widget-card__footer-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  color: var(--app-text-default);
  opacity: 0.55;
}

/* ── Activity list & rows ─────────────────────────────────── */
.ncrm-activity-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.ncrm-activity-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: calc(var(--app-radius-base) - 2px);
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.ncrm-activity-row:hover {
  background: rgba(var(--v-theme-on-surface), 0.05);
}

.ncrm-activity-row__icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  color: var(--app-text-default);
}

.ncrm-activity-row__icon--error {
  background: rgba(var(--v-theme-error), 0.1);
  color: rgb(var(--v-theme-error));
}

.ncrm-activity-row__icon--warning {
  background: rgba(var(--v-theme-warning), 0.1);
  color: rgb(var(--v-theme-warning));
}

.ncrm-activity-row__body {
  flex: 1;
  min-width: 0;
}

.ncrm-activity-row__title {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--app-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-activity-row__meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  color: var(--app-text-default);
  margin-top: 2px;
}

.ncrm-activity-row__sep {
  opacity: 0.4;
}

.ncrm-activity-row__date {
  opacity: 0.8;
}

.ncrm-activity-row__date--error {
  color: rgb(var(--v-theme-error));
  opacity: 1;
  font-weight: 600;
}

.ncrm-activity-row__company {
  flex-shrink: 0;
  font-size: 0.78rem;
  color: var(--app-text-default);
  opacity: 0.7;
  max-width: 110px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-activity-row__chevron {
  flex-shrink: 0;
  opacity: 0.3;
}

/* ── Smart Tracker ────────────────────────────────────────── */
.ncrm-tracker__timers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ncrm-tracker__timer {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border-radius: var(--app-radius-base);
}

.ncrm-tracker__timer--session {
  background: var(--app-text-strong);
  color: var(--app-content-background);
}

.ncrm-tracker__timer--day {
  background: rgba(var(--v-theme-on-surface), 0.05);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  color: var(--app-text-strong);
}

.ncrm-tracker__timer-eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.55;
}

.ncrm-tracker__timer--session .ncrm-tracker__timer-eyebrow {
  opacity: 0.65;
  color: inherit;
}

.ncrm-tracker__timer-value {
  font-family: var(--app-font-family-mono);
  font-size: 1.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.1;
}

.ncrm-tracker__label-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ncrm-tracker__label-eyebrow {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--app-text-default);
  opacity: 0.7;
}

.ncrm-tracker__contexts-eyebrow {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--app-text-default);
  opacity: 0.7;
}

.ncrm-tracker__context-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ncrm-tracker__context-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ncrm-tracker__context-name {
  font-size: 0.82rem;
  color: var(--app-text-default);
  flex: 0 0 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-tracker__context-track {
  flex: 1;
  height: 6px;
  border-radius: 99px;
  background: rgba(var(--v-theme-on-surface), 0.08);
  overflow: hidden;
}

.ncrm-tracker__context-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}

.ncrm-tracker__context-fill--primary {
  background: rgb(var(--v-theme-tertiary));
}

.ncrm-tracker__context-fill--neutral {
  background: rgba(var(--v-theme-on-surface), 0.25);
}

.ncrm-tracker__context-pct {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--app-text-default);
  flex: 0 0 40px;
  text-align: right;
}

/* ── Widget manager ───────────────────────────────────────── */
.ncrm-manager-section-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-text-default);
  opacity: 0.7;
}

.ncrm-widget-manager__list {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
  background: var(--app-content-background);
}

/* ── Responsive ───────────────────────────────────────────── */
@media (max-width: 960px) {
  .ncrm-board__header {
    align-items: flex-start;
  }

  .ncrm-board__header-right {
    width: 100%;
    justify-content: flex-end;
  }

  .ncrm-board__subtitle {
    display: none;
  }
}

@media (max-width: 900px) {
  .ncrm-board__kpi-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ncrm-board__activities {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .ncrm-board {
    padding: 16px 12px 32px;
  }

  .ncrm-board__kpi-strip {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .ncrm-tracker__timers {
    grid-template-columns: 1fr;
  }

  .ncrm-tracker__context-name {
    flex: 0 0 90px;
  }
}
</style>
